import { Button, Col, Modal, Stack } from "react-bootstrap";
import { QRCodeCanvas } from "qrcode.react";
import { PinInput } from "rsuite";
import logo from "../../assets/logo-square-notext.png";
import { useMemo, useState } from "react";
import { Clipboard, Download } from "react-bootstrap-icons";
import { toast } from "react-toastify";

export default function SettingsTwoFactorSetupModal({ otpUri, onClose, onComplete, backupCodes }) {
    const secret = useMemo(() => otpUri && new URL(otpUri).searchParams.get("secret"), [otpUri]);

    const [codesSaved, setCodesSaved] = useState(false);

    const copySecretToClipboard = async () => {
        try {
            await navigator.clipboard.writeText(secret);
            toast.success("Secret copied to clipboard");
        } catch {
            toast.error("Failed to copy secret to clipboard");
        }
    };

    const copyBackupCodesToClipboard = async () => {
        try {
            await navigator.clipboard.writeText(backupCodes.join("\n"));
            toast.success("Backup codes copied to clipboard");
            setCodesSaved(true);
        } catch {
            toast.error("Failed to copy backup codes to clipboard");
        }
    };

    const handleDownloadBackupCodes = () => {
        const blob = new Blob([backupCodes.join("\n")], { type: "text/plain;charset=utf-8" });
        const url = URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = url;
        link.download = "radioanalyzer-backup-codes.txt";
        document.body.appendChild(link);
        link.click();

        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        setCodesSaved(true);
    };

    return (
        <Modal show={!!otpUri} onHide={onClose} centered size="xl" fullscreen="md-down" backdrop="static">
            <Modal.Header closeButton>
                <Modal.Title>Two-Factor Authentication Setup</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Stack direction="horizontal" gap={5} className="d-flex flex-column flex-lg-row align-items-stretch">
                    <Col>
                        <Stack className="align-items-center d-none d-md-flex h-100" gap={4}>
                            <section>
                                <h5 className="mb-2">Step 1: Scan the QR code</h5>
                                <p className="mb-0">Open your authenticator app on your phone and scan the QR code. This will add your account to the app.</p>
                                <p className="text-body-secondary mt-1 mb-0">
                                    Supported apps include Google Authenticator, Microsoft Authenticator, Authy, and 1Password.
                                </p>
                            </section>
                            <QRCodeCanvas
                                value={otpUri}
                                className="m-auto"
                                size={248}
                                level="H"
                                fgColor="#142533"
                                imageSettings={{
                                    src: logo,
                                    height: 52,
                                    width: 52,
                                    excavate: true,
                                }}
                            />
                        </Stack>
                        <section className="d-md-none">
                            <h5 className="mb-2">Step 1: Setup manually (on this phone)</h5>
                            <p>
                                If you are setting up two-factor authentication on this phone, open your authenticator app and choose to manually add an
                                account.
                            </p>
                            <ul>
                                <li>
                                    <b>Account name:</b> RadioAnalyzer
                                </li>
                                <li>
                                    <b>Key:</b>
                                    <Button
                                        variant="ra-yellow"
                                        size="sm"
                                        className="ms-1 d-inline-flex align-items-center column-gap-1"
                                        onClick={copySecretToClipboard}>
                                        <Clipboard size={16} /> Copy secret to clipboard
                                    </Button>
                                </li>
                                <li>
                                    <b>Type:</b> Time-based (TOTP)
                                </li>
                            </ul>
                        </section>
                    </Col>
                    <div className="vr opacity-100 d-none d-lg-block"></div>
                    <Col>
                        <Stack className="align-items-center h-100" gap={4}>
                            <section>
                                <h5 className="mb-2">Step 2: Enter the verification code</h5>
                                <p className="mb-0">
                                    After adding the account, your authenticator app will generate a 6-digit code that changes every 30 seconds. Enter the
                                    current 6-digit code on the right to complete setup.
                                </p>
                            </section>
                            <PinInput size="lg" length={6} otp onComplete={onComplete} autoFocus />
                            <section className={backupCodes?.length > 0 ? "" : "text-body-tertiary"}>
                                <h5 className="mb-2">Step 3: Save backup codes</h5>
                                <p className="mb-0">
                                    Two-Factor authentication is now enabled. Save these codes in a secure place — they can be used to sign in if you lose
                                    access to your authenticator app. You will not be able to see them again.
                                </p>
                            </section>
                            {backupCodes?.length > 1 && (
                                <>
                                    <ul className=" align-self-stretch mb-0" style={{ columns: 2 }}>
                                        {backupCodes.map(code => (
                                            <li key={code}>{code}</li>
                                        ))}
                                    </ul>
                                    <Stack direction="horizontal" gap={3} className="justify-content-center">
                                        <Button
                                            variant="ra-yellow"
                                            size="sm"
                                            className="col d-flex align-items-center justify-content-center column-gap-2 text-nowrap"
                                            onClick={copyBackupCodesToClipboard}>
                                            <Clipboard size={16} />
                                            Copy to Clipboard
                                        </Button>
                                        <Button
                                            variant="ra-yellow"
                                            size="sm"
                                            className="col d-flex align-items-center justify-content-center column-gap-2 text-nowrap"
                                            onClick={handleDownloadBackupCodes}>
                                            <Download size={16} />
                                            Download File
                                        </Button>
                                    </Stack>
                                </>
                            )}
                        </Stack>
                    </Col>
                </Stack>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="ra-dark-blue-outline" onClick={onClose} disabled={!codesSaved}>
                    Backup codes saved, close
                </Button>
            </Modal.Footer>
        </Modal>
    );
}
